import { FC, useState } from 'react';
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, DollarSign, Receipt, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { ExpenseDialogs } from './ExpenseDialogs';

interface ExpenseCardProps {
  expense: {
    id: string;
    name: string;
    amount: number;
    type: string;
    category: string;
    description?: string;
    due_date?: string;
    payment_date?: string;
    payment_reference?: string;
    is_paid: boolean;
  };
}

export const ExpenseCard: FC<ExpenseCardProps> = ({ expense }) => {
  const queryClient = useQueryClient();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editedExpense, setEditedExpense] = useState(expense);

  const getTypeColor = (type: string) => {
    return type === 'fixed' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800';
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      hosting: 'bg-purple-100 text-purple-800',
      software: 'bg-green-100 text-green-800',
      marketing: 'bg-yellow-100 text-yellow-800',
      salaries: 'bg-pink-100 text-pink-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return colors[category] || colors.other;
  };

  const handlePaymentStatusChange = async (checked: boolean) => {
    try {
      console.log('Updating payment status for expense:', expense.id);
      const { error } = await supabase
        .from('platform_expenses')
        .update({ 
          is_paid: checked,
          payment_date: checked ? new Date().toISOString() : null 
        })
        .eq('id', expense.id);

      if (error) {
        console.error('Error updating payment status:', error);
        toast.error('Error al actualizar el estado del pago');
        throw error;
      }

      toast.success(checked ? 'Gasto marcado como pagado' : 'Gasto marcado como pendiente');
      queryClient.invalidateQueries({ queryKey: ['platform-expenses'] });
      queryClient.invalidateQueries({ queryKey: ['financial-summary'] });
    } catch (error) {
      console.error('Error in handlePaymentStatusChange:', error);
    }
  };

  const handleUpdate = () => {
    queryClient.invalidateQueries({ queryKey: ['platform-expenses'] });
    queryClient.invalidateQueries({ queryKey: ['financial-summary'] });
  };

  return (
    <>
      <Card className="p-4 hover:shadow-md transition-shadow">
        <div className="flex flex-col gap-3">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-medium text-gray-900">{expense.name}</h3>
              <div className="flex items-center gap-1 text-gray-500 text-sm">
                <DollarSign className="h-4 w-4" />
                <span>{expense.amount.toFixed(2)} USD</span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Badge variant="outline" className={getTypeColor(expense.type)}>
                {expense.type === 'fixed' ? 'Fijo' : 'Variable'}
              </Badge>
              <Badge className={getCategoryColor(expense.category)}>
                {expense.category}
              </Badge>
            </div>
          </div>

          {expense.description && (
            <p className="text-sm text-gray-600">{expense.description}</p>
          )}

          <div className="flex flex-col gap-2">
            {expense.due_date && (
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                <span>
                  Vence: {format(new Date(expense.due_date), 'dd MMM yyyy', { locale: es })}
                </span>
              </div>
            )}

            {expense.payment_date && (
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <Receipt className="h-4 w-4" />
                <span>
                  Pagado: {format(new Date(expense.payment_date), 'dd MMM yyyy', { locale: es })}
                  {expense.payment_reference && ` - Ref: ${expense.payment_reference}`}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-2 pt-2 border-t">
            <div className="flex items-center gap-2">
              <Switch
                checked={expense.is_paid}
                onCheckedChange={handlePaymentStatusChange}
                className="data-[state=checked]:bg-green-500"
              />
              <Badge variant={expense.is_paid ? "secondary" : "destructive"}>
                {expense.is_paid ? 'Pagado' : 'Pendiente'}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditDialogOpen(true)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDeleteDialogOpen(true)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <ExpenseDialogs
        expense={expense}
        isEditDialogOpen={isEditDialogOpen}
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsEditDialogOpen={setIsEditDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
        editedExpense={editedExpense}
        setEditedExpense={setEditedExpense}
        onUpdate={handleUpdate}
      />
    </>
  );
};